interface ErrorContext {
  component?: string;
  action?: string;
  errorType?: 'react-error' | 'unhandled-rejection' | 'window-error' | 'api-error' | 'console-error';
  componentStack?: string;
  metadata?: Record<string, any>;
}

const SESSION_TOKEN_KEY = "openbox_session_token";

function getSessionToken(): string | null {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

// Use fetch directly instead of apiRequest to avoid circular dependencies
// and to ensure errors can be reported even when the query client fails
async function sendErrorReport(payload: any): Promise<void> {
  try {
    await fetch('/api/errors/report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });
  } catch (e) {
    // Silent fail - don't want error reporting to cause more errors
    console.error('[ErrorReporter] Failed to send error:', e);
  }
}

export async function reportError(
  error: Error,
  context?: ErrorContext
): Promise<void> {
  try {
    const url = window.location.href;
    const sessionToken = getSessionToken();
    
    await sendErrorReport({
      message: error.message,
      stack: error.stack,
      url,
      sessionToken,
      errorType: context?.errorType || 'client',
      componentStack: context?.componentStack,
      metadata: {
        component: context?.component,
        action: context?.action,
        ...context?.metadata,
      },
    });
    
    console.log('[ErrorReporter] Error reported to backend');
  } catch (reportError) {
    console.error('[ErrorReporter] Failed to report error:', reportError);
  }
}

// Track reported errors to avoid duplicates
const reportedErrors = new Set<string>();

function getErrorKey(error: Error | string): string {
  const msg = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack?.substring(0, 200) : '';
  return `${msg}:${stack}`;
}

function shouldReport(error: Error | string): boolean {
  const key = getErrorKey(error);
  if (reportedErrors.has(key)) {
    return false;
  }
  reportedErrors.add(key);
  // Clear old entries after 1 minute to allow re-reporting recurring issues
  setTimeout(() => reportedErrors.delete(key), 60000);
  return true;
}

const isDomManipulationError = (error: Error | string): boolean => {
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return (
    message.includes('removechild') ||
    message.includes('insertbefore') ||
    message.includes('appendchild') ||
    message.includes('not a child of this node') ||
    message.includes('the node to be removed') ||
    (message.includes('failed to execute') && message.includes("on 'node'")) ||
    // Browser wallet extensions (Phantom, MetaMask, etc.) try to redefine window.ethereum
    message.includes('cannot redefine property: ethereum') ||
    message.includes('cannot redefine property: solana') ||
    // Microsoft Outlook SafeLink / Edge "read-aloud" extension noise. Emitted by an
    // injected helper when it can't find an ActiveX-style object — has nothing to do
    // with our app code.
    message.includes('object not found matching id')
  );
};

// Errors that originate from Google Ads / Tag Manager scripts (loaded from googletagmanager.com).
// These scripts use properties like `pageType` as custom GTM dimensions. In Safari, cross-origin
// errors from external ad/analytics scripts are sometimes attributed to the host page bundle,
// but they are not actionable and should be suppressed.
const isThirdPartyAnalyticsError = (error: Error | string): boolean => {
  const message = (error instanceof Error ? error.message : String(error));
  return (
    // Google Tag Manager / Ads: "pageType" is a standard GTM custom dimension
    message.includes("evaluating 'e.pageType'") ||
    message.includes("evaluating 'n.pageType'") ||
    message.includes('.pageType') ||
    // Generic: "cannot read properties of undefined" on well-known GTM variables
    (message.toLowerCase().includes('pagetype') && message.toLowerCase().includes('undefined'))
  );
};

// Errors originating from browser extensions (content scripts, injected
// inspectors, wallet extensions). The stack references an extension origin —
// nothing we can fix, so suppress from reporting.
const isBrowserExtensionError = (error: Error | string): boolean => {
  const stack = error instanceof Error ? (error.stack || '') : '';
  const message = error instanceof Error ? error.message : String(error);
  return (
    stack.includes('chrome-extension://') ||
    stack.includes('moz-extension://') ||
    stack.includes('safari-extension://') ||
    stack.includes('safari-web-extension://') ||
    // "Message Timeout" is emitted by extension messaging channels
    // (chrome.runtime.sendMessage) when the extension's background worker
    // doesn't answer — our app code never throws this exact message. The
    // rejection reason often arrives as a non-Error, so the reconstructed
    // stack points at our own handler frame; an origin/stack check is
    // therefore unreliable and the exact-message match is the right filter.
    message === 'Message Timeout' ||
    message.includes('Extension context invalidated')
  );
};

const isAuthRelatedError = (error: Error | string): boolean => {
  if (error instanceof Error && error.name === 'AuthRedirectError') return true;
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('Admin API access denied - suppressed') ||
    message.includes('Redirecting to home due to access denied') ||
    message.includes('Access denied on public page') ||
    message.includes('Redirecting due to authentication issue');
};

export function setupGlobalErrorHandlers(): void {
  // Capture uncaught errors
  window.onerror = (message, source, lineno, colno, error) => {
    const msgStr = String(message);

    // "Script error." is the browser's sanitized message for cross-origin script
    // errors (browser extensions, third-party resources). The browser strips all
    // detail by design — there is nothing actionable to report.
    if (msgStr === 'Script error.' || msgStr === 'Script error') {
      return false;
    }

    const err = error || new Error(msgStr);

    if (isDomManipulationError(err)) {
      console.warn('[ErrorHandler] Suppressed DOM manipulation error (likely browser extension):', err instanceof Error ? err.message : err);
      return true;
    }

    if (isThirdPartyAnalyticsError(err)) {
      // Suppress silently — these come from Google Ads/GTM scripts, not our code
      return true;
    }

    if (isBrowserExtensionError(err) || (source && (source.startsWith('chrome-extension://') || source.startsWith('moz-extension://')))) {
      // Suppress — originates from a browser extension, not our code
      return true;
    }

    if (shouldReport(err)) {
      reportError(err, {
        component: 'window',
        action: 'uncaught_error',
        errorType: 'window-error',
        metadata: { source, lineno, colno },
      });
    }
    return false;
  };

  // Capture unhandled promise rejections
  window.onunhandledrejection = (event) => {
    // Skip if reason is undefined/null - these are typically benign (canceled requests, component unmounts)
    if (event.reason === undefined || event.reason === null) {
      return;
    }
    
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    // Skip AuthRedirectError - these are intentional redirects, not real errors
    if (error.name === 'AuthRedirectError') {
      return;
    }

    if (isDomManipulationError(error)) {
      console.warn('[ErrorHandler] Suppressed DOM manipulation rejection (likely browser extension):', error.message);
      event.preventDefault();
      return;
    }

    if (isThirdPartyAnalyticsError(error)) {
      // Suppress silently — these come from Google Ads/GTM scripts, not our code
      event.preventDefault();
      return;
    }

    if (isAuthRelatedError(error)) {
      event.preventDefault();
      return;
    }

    if (isBrowserExtensionError(error)) {
      // Suppress — originates from a browser extension, not our code
      event.preventDefault();
      return;
    }

    if (shouldReport(error)) {
      reportError(error, {
        component: 'promise',
        action: 'unhandled_rejection',
        errorType: 'unhandled-rejection',
      });
    }
  };

  // Helper to stringify arguments, properly handling Error objects
  function stringifyArg(arg: unknown): string {
    if (arg instanceof Error) {
      // Error objects don't stringify well, extract the meaningful parts
      return arg.message || arg.toString();
    }
    if (typeof arg === 'object' && arg !== null) {
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    }
    return String(arg);
  }

  const SUPPRESSED_CONSOLE_PATTERNS = [
    '[Login] Failed for',
    '[Login] Exception',
    '[ErrorHandler] Suppressed',
  ];

  // Intercept console.error to capture all logged errors
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // Call original first
    originalConsoleError.apply(console, args);
    
    // Try to extract meaningful error info
    const firstArg = args[0];
    let error: Error;
    
    if (firstArg instanceof Error) {
      if (isDomManipulationError(firstArg) || isAuthRelatedError(firstArg) || isBrowserExtensionError(firstArg)) {
        return;
      }
      error = firstArg;
    } else if (typeof firstArg === 'string') {
      // Skip our own error reporter logs to avoid infinite loop
      if (firstArg.includes('[ErrorReporter]')) {
        return;
      }
      // Skip suppressed patterns (expected errors like failed logins)
      if (SUPPRESSED_CONSOLE_PATTERNS.some(p => firstArg.includes(p))) {
        return;
      }
      if (isDomManipulationError(firstArg) || isAuthRelatedError(firstArg)) {
        return;
      }
      error = new Error(args.map(stringifyArg).join(' '));
    } else {
      error = new Error(args.map(stringifyArg).join(' '));
    }

    // The constructed error's stack captures the caller of console.error —
    // if that's an extension content script, suppress.
    if (isBrowserExtensionError(error)) {
      return;
    }

    if (shouldReport(error)) {
      reportError(error, {
        component: 'console',
        action: 'console_error',
        errorType: 'console-error',
      });
    }
  };
}

// Report API errors from failed fetch requests
export function reportApiError(
  error: Error,
  url: string,
  method: string,
  status?: number
): void {
  if (shouldReport(error)) {
    reportError(error, {
      component: 'api',
      action: 'api_request_failed',
      errorType: 'api-error',
      metadata: { url, method, status },
    });
  }
}
